<template>
  <section class="box-layout">
    <div v-if="!isLoading" class="billing__content">
      <header class="content__header">
        <h2>{{ $t('userInfo.lnb.billing') }}</h2>
      </header>
      <div class="content__body" ref="scrollContent">
        <content-box class="payment-detail" :width="'370px'">
          <p class="title">{{ $t('billing.paymentDetail.title') }}</p>

          <template v-if="cardInfo">
            <div class="card-number--wrap">
              <span class="label">{{ $t('billing.paymentDetail.cardNumber') }}</span>
              <p class="value">
                <span>{{ cardInfo.cardNumber }} XXXX XXXX XXXX</span>
                <span class="card-name" :class="cardName()">
                <template v-if="cardName() === 'default'">
                  {{ cardInfo.cardName }}
                </template>
                <i v-else class="icon" :class="`icon--card-${cardName()}`"></i>
              </span>
              </p>
            </div>
            <div class="register-date--wrap">
              <span class="label">{{ $t('billing.paymentDetail.registeredDate') }}</span>
              <p class="value">{{ (cardInfo.updateDate || cardInfo.date) | dashDate }}</p>
            </div>
          </template>

          <template v-else>
            <p class="no-info">{{ $t('billing.paymentDetail.noRegistered') }}</p>
          </template>

          <div class="ui-btn--wrap">
            <div class="card-alert" v-if="cardInfo && !cardInfo.iamportCustomerUid">
              <i class="icon icon--alert-triangle-size16"></i>
              <p class="balloon" v-html="$t('billing.paymentDetail.needUpdate')"></p>
            </div>
            <ui-button v-if="cardInfo"
                       :size="'small'"
                       :primary2="true"
                       @click="onRegisterCard">{{ $t('billing.paymentDetail.update') }}</ui-button>
            <ui-button v-if="!cardInfo"
                       :size="'small'"
                       :primary2="true"
                       :disabled="$i18n.locale !== 'ko' "
                       @click="onRegisterCard">{{ $t('billing.paymentDetail.register') }}</ui-button>
          </div>
        </content-box>

        <content-box class="billing-detail">
          <p class="title">{{ $t('billing.billingDetail.title') }}</p>
          <div class="table--wrap" ref="listTable">
            <app-pagination class="billing-detail-pagination clearfix"
                            :total="paymentHistoryCount"
                            :page="page"
                            :limit="limit"
                            @onPagingOptionChange="onPagingOptionChange">
            </app-pagination>

            <data-table
              :headers="headers">
              <template v-slot:colgroup>
                <colgroup>
                  <col width="20%">
                  <col width="20%">
                  <col width="20%">
                  <col width="20%">
                  <col width="20%">
                </colgroup>
              </template>
              <template v-slot>
                <tbody v-if="paymentHistoryCount">
                <tr v-for="(item, index) in paymentHistoryList" :key="index">
                  <td class="data__date">{{ item.paymentDate | dashDate }}</td>
                  <td class="data__payment-item">{{ item.data.orderName }}</td>
                  <td class="data__method">
                    <template v-if="cardInfo">
                      <i v-if="item.cardNumber === cardInfo.cardNumber"
                         class="icon"
                         :class="`icon--card-${cardName()}`"></i>
                    </template>
                    <span class="card-num">{{ item.cardNumber }} XXXX XXXX XXXX</span>
                  </td>
                  <td class="data__total">₩{{ item.price | thousand }}</td>
                  <td class="data__receipt">
                    <ui-button :primary3="true" @click="onShowReceipt(item)">
                      {{ $t('common.view') }}
                    </ui-button>
                  </td>
                </tr>
                </tbody>
                <tbody v-else>
                <tr>
                  <td class="no-list" colspan="4">
                    {{ $t('billing.billingDetail.noList') }}
                  </td>
                </tr>
                </tbody>
              </template>
            </data-table>
          </div>
        </content-box>
        <back-to-top :refName="'scrollContent'" />
      </div>
      <div v-show="showReceiptLayer" class="dimmed">
        <content-box class="receipt--wrap" :width="'480px'">
          <p class="title">{{ $t('billing.invoice') }}</p>
          <iframe v-if="receiptUrl" ref="billIframe" id="billIframe"
                  style="width: 420px; height: 540px;"
                  :src="receiptUrl"></iframe>
          <i class="icon icon--close-size16" @click="onCloseReceiptLayer"></i>
        </content-box>
      </div>
      <register-card v-if="registerData"
                     :userData="userData"
                     :registerData="registerData"
                     @onRegisterSuccess="onRegisterSuccess"
                     @onRegisterCanceled="onRegisterCanceled"
                     @onRegisterFailed="onRegisterFailed"/>
    </div>
  </section>
</template>

<script>
import ContentBox from '@/components/ui/ContentBox'
import AppPagination from '@/components/ui/AppPagination'
import DataTable from '@/components/ui/DataTable'
import BackToTop from '@/components/ui/BackToTop'
import UiButton from '@/components/ui/UiButton'
import RegisterCard from '@/components/user-info/manage-plan/RegisterCard'
import { getCardInfo, getPaymentHistory, registerSuccessToss } from '@/api/card'
import { mapState } from 'vuex'
import { getDialog } from '@/libs/vue-utils'
import { EventBus } from '@/libs/event-bus'
import axios from 'axios'

const openDialog = getDialog()

export default {
  components: {
    ContentBox,
    AppPagination,
    DataTable,
    BackToTop,
    UiButton,
    RegisterCard
  },
  data () {
    return {
      isLoading: true,
      showReceiptLayer: false,
      receiptUrl: null,
      cardInfo: null,
      paymentHistory: null,
      page: 1,
      limit: 30,
      registerData: null
    }
  },
  watch: {
    showReceiptLayer (value) {
      if (value) {
        let body = document.querySelector('body')
        body.style.overflow = 'hidden'
      } else {
        let body = document.querySelector('body')
        body.style.overflow = 'initial'
      }
    }
  },
  computed: {
    headers () {
      return [
        { name: this.$t('common.date') },
        { name: this.$t('common.item') },
        { name: this.$t('billing.paymentDetail.paymentMethod') },
        { name: this.$t('common.total') },
        { name: this.$t('billing.invoice') }
      ]
    },
    ...mapState([
      'userData'
    ]),
    paymentHistoryList () {
      if (!this.paymentHistory) {
        return []
      }
      const startIndex = (this.page - 1) * this.limit
      const endIndex = startIndex + this.limit
      return this.paymentHistory.slice(startIndex, endIndex)
    },
    paymentHistoryCount () {
      return this.paymentHistory ? this.paymentHistory.length : 0
    }
  },
  methods: {
    cardName (cardName) {
      if (!cardName) {
        cardName = this.cardInfo.cardName
      }
      let className = 'default'
      if (cardName.indexOf('비씨 || BC') > -1 || cardName.indexOf('비씨') > -1 || cardName.indexOf('BC') > -1) {
        className = 'bc'
      } else if (cardName.indexOf('우리') > -1) {
        className = 'woori'
      } else if (cardName.indexOf('삼성') > -1) {
        className = 'samsung'
      } else if (cardName.indexOf('하나') > -1) {
        className = 'hana'
      } else if (cardName.indexOf('신한') > -1) {
        className = 'shinhan'
      } else if (cardName.indexOf('롯데') > -1) {
        className = 'lotte'
      } else if (cardName.indexOf('현대') > -1) {
        className = 'hyundai'
      } else if (cardName.indexOf('국민') > -1) {
        className = 'kb'
      } else if (cardName.indexOf('농협') > -1) {
        className = 'nh'
      } else if (cardName.indexOf('씨티') > -1 || cardName.indexOf('citi') > -1) {
        className = 'citi'
      } else {
        className = 'default'
      }

      return className
    },
    setTossCard () {
      if (this.$route.query.customerKey && this.$route.query.authKey) {
        let secretKey = process.env.VUE_APP_TOSS_API_BILLLING_SEC
        secretKey = Buffer.from(secretKey).toString('base64')
        const url = process.env.VUE_APP_TOSS_API_URL + '/v1/billing/authorizations/issue'

        const headers = {
          Authorization: 'Basic ' + secretKey, // 예시 토큰
          'Content-Type': 'application/json'
        }
        axios.post(url, this.$route.query, { headers }).then(resp => {
          resp.data.isToss = true
          registerSuccessToss(resp.data).then(() => {
            this.setCardInfo()
            this.$router.push({ name: 'billing', query: {} })
          })
        })
      } else if (this.$route.query.code && this.$route.query.message) {
        openDialog({
          dialogType: 'plan.registerCardFailed',
          ok: this.$t('common.confirm')
        }).then(() => {
          this.$router.push({ name: 'billing', query: {} })
        })
      }
    },
    setCardInfo () {
      getCardInfo('ba').then(response => {
        this.cardInfo = response
      })
    },
    setPaymentHistory () {
      getPaymentHistory().then(response => {
        this.paymentHistory = []
        for (let history of response) {
          if (history.cardNumber !== 'WIRE' && history.cardNumber !== '4grit' && history.cardNumber !== 'jam') {
            this.paymentHistory.push(history)
          }
        }
      })
    },
    onPagingOptionChange (page, limit) {
      this.page = page
      this.limit = limit
    },
    onShowReceipt (item) {
      this.showReceiptLayer = true
      if (item.data.pg_tid.indexOf('INIpayBillMOI4gritco') === 0) {
        this.receiptUrl = 'https://iniweb.inicis.com/DefaultWebApp/mall/cr/cm/mCmReceipt_head.jsp?noTid=' + item.paymentNum + '&noMethod=1'
      } else {
        this.receiptUrl = item.data.receipt.url
      }
    },
    onCloseReceiptLayer () {
      this.receiptUrl = null
      this.showReceiptLayer = false
    },
    loadData () {
    },
    onRegisterCard (isToss = null) {
      this.registerData = {
        customerUid: this.cardInfo ? this.cardInfo.iamportCustomerUid : null,
        isToss: isToss
      }
    },
    onRegisterSuccess (result) {
      this.registerData = null
      this.cardInfo = result
    },
    onRegisterCanceled () {
      this.registerData = null
    },
    onRegisterFailed () {
      this.registerData = null

      if (this.cardInfo) {
        openDialog({
          dialogType: 'plan.changeCardFailed',
          ok: this.$t('common.confirm')
        })
      } else {
        openDialog({
          dialogType: 'plan.registerCardFailed',
          ok: this.$t('common.confirm')
        })
      }
    },
    handleBillingAction (action) {
      switch (action) {
        case 'registerCard':
          this.onRegisterCard()
          break
      }
    }
  },
  created () {
    Promise.all([
      this.setTossCard(),
      this.setCardInfo(),
      this.setPaymentHistory()
    ]).then(() => {
      EventBus.$emit('onLoading', false)
      this.isLoading = false
    }).catch(() => {
      EventBus.$emit('onLoading', false)
      this.isLoading = false
    })

    setTimeout(() => {
      const action = this.$route.params.action

      if (action) {
        this.handleBillingAction(action)
      }
    }, 300)

    EventBus.$on('onActionTrigger', (triggeredAction) => {
      if (triggeredAction) {
        this.handleBillingAction(triggeredAction)
      }
    })
  },
  beforeRouteUpdate (to, from, next) {
    const action = to.params.action

    if (action) {
      this.handleBillingAction(action)
    }
    next()
  }
}
</script>

<style lang="scss" scoped="scoped">
  .box-layout {
    height: 100%;
  }

  .billing__content {
    padding-top: 40px;
    height: 100%;
    box-sizing: border-box;

    .content__header {
      font-size: 26px;
      font-weight: 600;
      color: #444444;
      margin-left: 30px;
      margin-right: 30px;
      padding-bottom: 22px;
      border-bottom: 1px solid #d7d7d7;

      h2 {
        padding-left: 10px;
      }
    }

    .content__body {
      overflow-y: auto;
      height: calc(100% - 79px);
      padding: 30px 30px 0;

      .content--wrap {
        border-radius: 8px;

        .title {
          font-size: 15px;
          font-weight: 500;
          color: #2f2f2f;
        }
      }

      .payment-detail {
        position: relative;
        padding: 25px 20px 30px 25px;
        .label {
          font-size: 12px;
          color: #767676;
        }
        .value {
          margin-top: 8px;
        }

        .card-number--wrap {
          margin-top: 58px;
          .value {
            @include flexXbetween;
            align-items: center;
            font-size: 17px;
            font-weight: 500;
            color: #444444;

            .card-name {
              text-align: right;
              width: 115px;
              font-size: 14px;
              line-height: 20px;

              display: -webkit-box;
              -webkit-line-clamp: 3;
              -webkit-box-orient: vertical;
              overflow: hidden;

              .icon {
                width: 80px;
                height: 22px;
                background-size: contain;
              }
            }
          }
        }
        .register-date--wrap {
          margin-top: 22px;
          .value {
            font-size: 14px;
            color: #2f2f2f;
          }
        }

        .ui-btn--wrap {
          position: absolute;
          right: 20px;
          bottom: 20px;

          /deep/ .ui-btn {
            margin-left: 5px;

            .text {
              font-size: 13px;
            }
          }

          .card-alert {
            position: relative;
            top: 10px;
            right: 5px;
            float: left;

            .balloon {
              position: absolute;
              bottom: 33px;
              left: 50%;
              width: 232px;
              margin-left: -133px;
              color: #444444;
              line-height: 1.46;
              font-size: 13px;
              display: none;
              padding: 15px 17px;
              background: #fff;
              border-radius: 10px;
              box-shadow: 0 8px 15px 0 rgba(0, 0, 0, 0.16), 0 0 0 1px rgba(0, 0, 0, 0.16);

              &::after {
                content: "";
                background: #fff;
                border-right: 1px solid rgba(0, 0, 0, 0.27);
                border-bottom: 1px solid rgba(0, 0, 0, 0.27);
                width: 10px;
                height: 10px;
                margin-left: -5px;
                position: absolute;
                bottom: -6px;
                left: 50%;
                transform: rotate(45deg);
              }
            }

            &:hover .balloon {
              display: block;
            }
          }
        }

        .no-info {
          margin-top: 63px;
          margin-bottom: 82px;
          text-align: center;
        }

      }

      .billing-detail {
        margin-top: 30px;
        padding: 25px 25px 30px;

        .title {
          padding-bottom: 20px;
        }

        .billing-detail-pagination {
          margin-bottom: 15px;
        }

        .table--wrap {
          text-align: center;

          /deep/ thead {
            font-size: 13px;
          }

          tbody tr {
            border-bottom: 1px solid #ebebeb;

            td {
              height: 50px;
              font-size: 13px;
              line-height: 1.23;
              color: #2f2f2f;

              &.no-list {
                height: 199px;
                color: #767676;
              }

              &.data__method {
                .icon {
                  width: 80px;
                  height: 22px;
                  background-size: contain;
                  vertical-align: middle;
                  margin-right: 5px;
                }
                .card-num {
                  display: inline-block;
                  vertical-align: middle;
                }
              }

              &.data__receipt {
                .ui-btn.size--normal {
                  height: 34px;
                  font-size: 13px;
                  min-width: 80px;
                }
              }
            }
          }
        }
      }
    }

    .dimmed {
      .receipt--wrap {
        @include positionXYcenter;
        text-align: center;
        padding: 30px;

        .title {
          text-align: left;
          font-size: 15px;
          font-weight: 500;
          color: #2f2f2f;
          margin-bottom: 25px;
        }

        .icon {
          position: absolute;
          top: 12px;
          right: 12px;
        }
      }

      .beusable-card--wrap {
        @include positionXYcenter;
        text-align: left;
        padding: 30px;

        .ui-btn--wrap {
          text-align: center;

          /deep/ .ui-btn {
            margin-left: 5px;
            min-width: 90px;
          }
        }

        .title {
          font-size: 15px;
          font-weight: 500;
          color: #2f2f2f;
          margin-bottom: 8px;
        }

        .description {
          font-size: 12px;
          color: #2f2f2f;
          line-height: 1.4;
        }

        .card-info--wrap {
          width: 333px;
          height: 207px;
          margin: 36px auto 40px auto;
          padding: 0 23px;
          box-sizing: border-box;
          border-radius: 7px;
          box-shadow: 0 21px 14px -11px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.08);
          border: solid 1px #e4e4e4;
          background-color: #ffffff;
        }

        .card-number--wrap {
          margin-top: 58px;

          .value {
            @include flexXbetween;
            align-items: center;
            font-size: 15px;
            font-weight: 500;
            color: #444444;

            .card-name {
              text-align: right;
              width: 115px;
              font-size: 14px;
              line-height: 20px;

              display: -webkit-box;
              -webkit-line-clamp: 3;
              -webkit-box-orient: vertical;
              overflow: hidden;

              .icon {
                width: 80px;
                height: 22px;
                background-size: contain;
              }
            }
          }
        }

        .register-date--wrap {
          margin-top: 22px;

          .value {
            margin-top: 5px;
            font-size: 14px;
            color: #2f2f2f;
          }
        }
      }
    }
  }
</style>
